import { useCallback } from 'react';
import { getTableColumns } from '../cols-definition';
import { useDynamicSearch } from '@/hooks';
import { SearchSchema } from '../schemas';
import { QUERY_KEYS } from '@/constants';

interface UseSearchTableProps {
  searchData: (filters: Record<string, any>, page?: number, searchPageSize?: number) => Promise<void>;
  searchParams: SearchSchema;
  setSearchParams: (params: SearchSchema) => void;
  loading: boolean;
  handleViewClick: (obj: any) => void;
  sumTotal: number | null;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({
  searchData,
  searchParams,
  setSearchParams,
  loading,
  handleViewClick,
  sumTotal,
  isFiltered,
  handlePageChange
}: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: any) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: any, onSearchFn: (params: any) => Promise<void>) => {
      const { dynamic_filters, ...searchParamsOnly } = formData;
      setSearchParams(searchParamsOnly as SearchSchema);
      await onSearchFn(formData);
    },
    [setSearchParams]
  );

  const getColumns = useCallback(() => {
    const cols = getTableColumns(handleViewClick, sumTotal, loading);
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, [handleViewClick, sumTotal, loading]);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: searchParams,
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.PHIEU_KE_TOAN}/filter`,
    pageSize: 20,
    sumField: 't_ps_no_nt'
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData(searchParams, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchParams, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
